import React from 'react';
import Icon from '../../../components/AppIcon';
import { Search, AlertTriangle, Loader2 } from 'lucide-react';

const VehicleDataTable = ({ 
  vehicles = [], 
  loading = false, 
  error = null, 
  isScanning = false,
  totalVehicles = 0 
}) => {
  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return {
      date: date?.toLocaleDateString('es-MX'),
      time: date?.toLocaleTimeString('es-MX', { 
        hour: '2-digit', 
        minute: '2-digit',
        second: '2-digit'
      })
    };
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString);
      return date?.toLocaleString('es-ES', {
        day: '2-digit',
        month: '2-digit', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Fecha inválida';
    }
  };

  const getStatusBadge = (status) => {
    if (status === 'active') {
      return <span className="bg-green-100 text-green-800 border border-green-200">Activo</span>;
    } else if (status === 'inactive') {
      return <span className="bg-gray-100 text-gray-800 border border-gray-200">Inactivo</span>;
    } else {
      return <span className="bg-gray-100 text-gray-800 border border-gray-200">Desconocido</span>;
    }
  };

  const SkeletonRow = () => (
    <tr className="border-b border-border">
      {Array.from({ length: 7 })?.map((_, index) => (
        <td key={index} className="px-4 py-3">
          <div className="h-4 bg-muted rounded animate-pulse"></div>
        </td>
      ))}
    </tr>
  );

  const VehicleCard = ({ vehicle }) => {
    const { date, time } = formatDateTime(vehicle?.detectedAt);
    
    return (
      <div className="bg-card border border-border rounded-lg p-4 shadow-minimal">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <Icon name="Car" size={20} className="text-primary" />
            <span className="font-semibold text-foreground">{vehicle?.plate}</span>
          </div>
          <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded">
            ID: {vehicle?.id}
          </span>
        </div>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <span className="text-xs text-muted-foreground block">Serie</span>
            <span className="text-sm font-medium text-foreground">{vehicle?.serialNumber}</span>
          </div>
          <div>
            <span className="text-xs text-muted-foreground block">Marca</span>
            <span className="text-sm font-medium text-foreground">{vehicle?.make}</span>
          </div>
          <div>
            <span className="text-xs text-muted-foreground block">Modelo</span>
            <span className="text-sm font-medium text-foreground">{vehicle?.model}</span>
          </div>
          <div>
            <span className="text-xs text-muted-foreground block">Año</span>
            <span className="text-sm font-medium text-foreground">{vehicle?.year}</span>
          </div>
        </div>
        <div className="flex items-center justify-between pt-3 border-t border-border">
          <div className="flex items-center gap-2">
            <Icon name="Clock" size={14} className="text-muted-foreground" />
            <span className="text-xs text-muted-foreground">{date}</span>
          </div>
          <span className="text-xs font-mono text-foreground">{time}</span>
        </div>
      </div>
    );
  };

  if (error) {
    return (
      <div className="bg-card border border-border rounded-lg p-6 shadow-card">
        <div className="flex items-center justify-center py-8">
          <div className="text-center">
            <Icon name="AlertTriangle" size={48} className="text-error mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-foreground mb-2">Error al cargar datos</h3>
            <p className="text-muted-foreground">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border shadow-sm">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-foreground">
              {isScanning ? 'Vehículos Detectados en Sesión' : 'Datos de Vehículos'}
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              {isScanning 
                ? `Mostrando ${vehicles?.length} vehículo(s) detectado(s) durante el escaneo activo`
                : `Lista completa de vehículos desde el Reader REFID (${vehicles?.length} de ${totalVehicles})`
              }
            </p>
          </div>
          
          {/* API Status Indicator */}
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-green-500" />
            <span className="text-sm text-muted-foreground">SDK de Reader REFID</span>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        {loading && (
          <div className="flex items-center justify-center p-8">
            <div className="flex items-center gap-3">
              <div className="animate-spin">
                <Loader2 className="w-5 h-5 text-muted-foreground" />
              </div>
              <span className="text-muted-foreground">
                {isScanning ? 'Escaneando vehículos...' : 'Cargando datos de la API...'}
              </span>
            </div>
          </div>
        )}

        {error && (
          <div className="p-8">
            <div className="flex items-center justify-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              <span>{error}</span>
            </div>
          </div>
        )}

        {!loading && !error && vehicles?.length === 0 && (
          <div className="p-8 text-center">
            <div className="flex flex-col items-center gap-3">
              <Search className="w-12 h-12 text-muted-foreground" />
              <div>
                <h4 className="font-medium text-foreground">
                  {isScanning ? 'Sin Detecciones' : 'No hay vehículos'}
                </h4>
                <p className="text-sm text-muted-foreground">
                  {isScanning 
                    ? 'Esperando detección de nuevos vehículos...'
                    : 'No se encontraron vehículos en el Reader REFID'
                  }
                </p>
              </div>
            </div>
          </div>
        )}

        {!loading && !error && vehicles?.length > 0 && (
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left p-4 font-medium text-foreground">ID</th>
                <th className="text-left p-4 font-medium text-foreground">No. Serie</th>
                <th className="text-left p-4 font-medium text-foreground">Placa</th>
                <th className="text-left p-4 font-medium text-foreground">Marca</th>
                <th className="text-left p-4 font-medium text-foreground">Modelo</th>
                <th className="text-left p-4 font-medium text-foreground">Año</th>
                <th className="text-left p-4 font-medium text-foreground">Estado</th>
                <th className="text-left p-4 font-medium text-foreground">
                  {isScanning ? 'Detectado' : 'Actualizado'}
                </th>
                <th className="text-left p-4 font-medium text-foreground">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {vehicles?.map((vehicle, index) => (
                <tr 
                  key={vehicle?.documentId || vehicle?.id || index}
                  className={`border-b border-border hover:bg-muted/25 ${
                    isScanning && index === 0 ? 'bg-green-50 border-green-200' : ''
                  }`}
                >
                  <td className="p-4 font-mono text-sm">
                    {vehicle?.id || 'N/A'}
                  </td>
                  <td className="p-4">
                    <span className="font-medium">{vehicle?.serialNumber || 'N/A'}</span>
                  </td>
                  <td className="p-4">
                    <span className="font-mono font-semibold text-blue-600">
                      {vehicle?.plate || 'N/A'}
                    </span>
                  </td>
                  <td className="p-4 capitalize">
                    {vehicle?.make?.toLowerCase() || 'Desconocido'}
                  </td>
                  <td className="p-4 capitalize">
                    {vehicle?.model?.toLowerCase() || 'Desconocido'}
                  </td>
                  <td className="p-4">
                    {vehicle?.year || 'N/A'}
                  </td>
                  <td className="p-4">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      vehicle?.flag 
                        ? 'bg-green-100 text-green-800 border border-green-200' :'bg-gray-100 text-gray-800 border border-gray-200'
                    }`}>
                      {vehicle?.flag ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    {formatDate(vehicle?.detectedAt || vehicle?.updatedAt)}
                  </td>
                  <td className="p-4">
                    <button 
                      className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md transition-colors duration-200"
                      onClick={() => {
                        // Mock function - no functionality for now
                        console.log('Iniciar Peritaje clicked for vehicle:', vehicle?.plate);
                      }}
                    >
                      Iniciar Peritaje
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Footer with additional info */}
      {!loading && !error && vehicles?.length > 0 && (
        <div className="p-4 border-t border-border bg-muted/25">
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span>
              {isScanning 
                ? `${vehicles?.length} vehículo(s) detectado(s) en esta sesión`
                : `Mostrando ${vehicles?.length} de ${totalVehicles} vehículos`
              }
            </span>
            <span className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full bg-green-500" />
              Datos de SDK Reader REFID
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default VehicleDataTable;